import type { User } from '@/types'

export type Module = {
  id: string
  label: string
  paths: string[]
}

export const MODULES: readonly Module[] = [
  { id: 'dashboard', label: 'Dashboard', paths: ['/'] },
  { id: 'pipeline', label: 'Pipeline', paths: ['/pipeline'] },
  { id: 'leads', label: 'Leads', paths: ['/leads'] },
  { id: 'proposals', label: 'Propostas', paths: ['/proposals'] },
  { id: 'catalog', label: 'Produtos', paths: ['/products'] },
  { id: 'price_tables', label: 'Tabelas de Preço', paths: ['/price-tables'] },
  { id: 'content_hub', label: 'Content Hub', paths: ['/content', '/social'] },
  { id: 'opportunities', label: 'Oportunidades', paths: ['/opportunities'] },
  { id: 'email_marketing', label: 'Email Marketing', paths: ['/email-marketing'] },
  { id: 'outreach', label: 'Outreach', paths: ['/outreach'] },
  { id: 'marketing_outbound', label: 'Marketing Outbound', paths: ['/marketing'] },
  { id: 'customer_success', label: 'Customer Success', paths: ['/cs'] },
  { id: 'campaigns', label: 'Campanhas', paths: ['/campaigns'] },
  { id: 'analytics', label: 'Analytics', paths: ['/analytics'] },
  { id: 'users', label: 'Usuários', paths: ['/users'] },
  { id: 'settings', label: 'Configurações', paths: ['/settings'] },
  { id: 'sales_dataroom', label: 'Sales Data Room', paths: ['/sales-dataroom'] },
  { id: 'assessments', label: 'Assessments', paths: ['/assessments-admin'] },
  { id: 'nai_desk', label: 'Nai Desk', paths: ['/nai-desk'] },
] as const

export const ALL_MODULE_IDS: string[] = MODULES.map((m) => m.id)

const UNRESTRICTED_HIERARCHY_LEVEL = 100

// Permissões de role que concedem módulos automaticamente.
// Mantém o opt-in por usuário (allowed_modules em attributes) e adiciona um union
// com os módulos derivados das permissões do role.
const ROLE_PERMISSION_TO_MODULES: Record<string, string[]> = {
  'nai_desk.admin': ['nai_desk'],
  'nai_desk.user': ['nai_desk'],
}

function rolePermissions(user: User | null | undefined): string[] {
  const perms = (user?.role as { permissions?: unknown } | undefined)?.permissions
  return Array.isArray(perms) ? perms.filter((p): p is string => typeof p === 'string') : []
}

function modulesFromRolePermissions(user: User | null | undefined): string[] {
  const perms = rolePermissions(user)
  if (perms.includes('*')) return ALL_MODULE_IDS
  const out = new Set<string>()
  for (const p of perms) {
    for (const m of ROLE_PERMISSION_TO_MODULES[p] ?? []) out.add(m)
  }
  return Array.from(out)
}

export function getAllowedModules(user: User | null | undefined): string[] {
  if (!user) return []
  if ((user.role?.hierarchy_level ?? 0) >= UNRESTRICTED_HIERARCHY_LEVEL) return ALL_MODULE_IDS

  const fromRole = modulesFromRolePermissions(user)
  if (fromRole === ALL_MODULE_IDS) return ALL_MODULE_IDS

  const stored = (user.attributes as { allowed_modules?: unknown } | undefined)?.allowed_modules
  const fromAttrs = Array.isArray(stored)
    ? stored.filter((s): s is string => typeof s === 'string')
    : ALL_MODULE_IDS

  // União: módulos explícitos do usuário + módulos implícitos do role
  return Array.from(new Set([...fromAttrs, ...fromRole]))
}

export function hasModuleAccess(user: User | null | undefined, moduleId: string): boolean {
  return getAllowedModules(user).includes(moduleId)
}

export function moduleForPath(pathname: string): string | null {
  const candidates = MODULES.flatMap((m) => m.paths.map((p) => ({ id: m.id, path: p })))
  // sort by path length desc; '/' last
  candidates.sort((a, b) => {
    if (a.path === '/') return 1
    if (b.path === '/') return -1
    return b.path.length - a.path.length
  })
  for (const c of candidates) {
    if (c.path === '/' && pathname === '/') return c.id
    if (c.path !== '/' && (pathname === c.path || pathname.startsWith(c.path + '/'))) return c.id
  }
  return null
}

export function pathIsAllowed(user: User | null | undefined, pathname: string): boolean {
  // Always-allowed routes (profile, login flow, public pages handled by router)
  if (pathname === '/profile' || pathname.startsWith('/profile/')) return true
  const moduleId = moduleForPath(pathname)
  if (!moduleId) return true // unknown route → don't block
  return hasModuleAccess(user, moduleId)
}

export function firstAllowedPath(user: User | null | undefined): string | null {
  const allowed = getAllowedModules(user)
  for (const m of MODULES) {
    if (allowed.includes(m.id)) return m.paths[0]
  }
  return null
}
